/**
 * useStep0ItemInitiation — composes the three Step-0 sub-flow hooks
 * into a clean `Step0BodyProps` contract, ready to spread onto
 * `<Step0ItemInitiation>`.
 *
 * Composition:
 *   - `useUploadFlow`        → Automatic mode
 *   - `useManualItemDefinition` → Manual mode (lifecycle gated by
 *                                  active mode? — see below)
 *   - `useDiagramItemDefinition` → Diagram mode (lifecycle gated)
 *
 * The Manual + Diagram hooks initialise lazily-ish:
 *   - Manual hook always runs (its localStorage seed is harmless +
 *     small). Submit only fires on user click.
 *   - Diagram hook fetches its list only when `enabled === true`
 *     (mode === 'diagram'). This avoids a network call for users
 *     who never pick that mode.
 *
 * `selectedMode` is read off the reducer's Step 0 slot. The mode
 * chooser dispatches `actions.setStepMode(0, mode)` via the
 * returned `onSelectMode` callback.
 */
import { useCallback } from 'react';
import { useUploadFlow } from '../hooks/useUploadFlow';
import { useManualItemDefinition } from '../hooks/useManualItemDefinition';
import { useDiagramItemDefinition } from '../hooks/useDiagramItemDefinition';
import type { Step0BodyProps } from '../step-components/stepBodyProps';
import type {
  PerStepState,
  Step0Mode,
  WorkflowActions,
} from '../state/workflowV2Types';

const UPLOAD_STEP_ID = 0;

export interface UseStep0ItemInitiationInput {
  sessionId: string | null | undefined;
  tenantId: string | null | undefined;
  actions: WorkflowActions;
  stepState: PerStepState | undefined;
  onThreadIdResolved: (threadId: string) => void;
}

export function useStep0ItemInitiation({
  sessionId,
  tenantId,
  actions,
  stepState,
  onThreadIdResolved,
}: UseStep0ItemInitiationInput): Step0BodyProps {
  const status = stepState?.status ?? 'empty';
  const selectedMode = stepState?.selectedMode ?? null;

  const upload = useUploadFlow({
    sessionId,
    tenantId,
    actions,
    onThreadIdResolved,
    status,
    errorMessage: stepState?.errorMessage ?? null,
  });

  const manual = useManualItemDefinition({
    sessionId,
    tenantId,
    actions,
    onThreadIdResolved,
  });

  const diagram = useDiagramItemDefinition({
    sessionId,
    tenantId,
    actions,
    onThreadIdResolved,
    enabled: selectedMode === 'diagram',
  });

  const onSelectMode = useCallback(
    (mode: Step0Mode) => {
      actions.setStepMode(UPLOAD_STEP_ID, mode);
    },
    [actions],
  );

  return {
    status,
    selectedMode,
    upload,
    manual,
    diagram,
    onSelectMode,
  };
}
